import { useCallback, useState } from "react"
import { toast } from "sonner"
import { useThreadRuntime } from "@assistant-ui/react"
import { usePageContext } from "./use-page-context"
import { useChatPanel } from "./use-chat-panel"
import {
  postChatRequest,
  readSseEvents,
  type StreamContent,
} from "./use-chat-runtime"

/** Mirror of ``debugLog`` in ``use-chat-runtime``. Toggle with
 *  ``localStorage.setItem('chat:debug', '1')`` from DevTools. */
function debugLog(scope: string, ...args: unknown[]): void {
  try {
    if (localStorage.getItem("chat:debug") !== "1") return
  } catch {
    return
  }
  console.log(`[chat:${scope}]`, ...args)
}

/**
 * Fire a submit-intent turn against the chat endpoint. Accumulates the SSE
 * response, then appends it as a single assistant message to the current
 * thread runtime.
 *
 * - ``mode="submit"`` — first click. The AI either emits a confirm UI block
 *   (happy path, button stays idle and the user clicks Yes in the card) or
 *   asks ONE clarifying question (button arms for a second click).
 * - ``mode="submit_force"`` — second click after arming. The AI emits the
 *   confirm block regardless of gaps.
 */
export function useSubmitIntent() {
  const threadRuntime = useThreadRuntime()
  const pageContext = usePageContext()
  const threadId = useChatPanel((s) => s.threadId)
  const setThreadId = useChatPanel((s) => s.setThreadId)
  const setSubmitMode = useChatPanel((s) => s.setSubmitMode)
  const markAssistantReplied = useChatPanel((s) => s.markAssistantReplied)

  const [isRunning, setIsRunning] = useState(false)

  const trigger = useCallback(
    async (mode: "submit" | "submit_force") => {
      if (isRunning) return
      setIsRunning(true)

      try {
        const response = await postChatRequest({
          user_message: "",
          thread_id: threadId,
          context_order_id: pageContext.orderId,
          context_project_id: pageContext.projectId,
          context_page: pageContext.path,
          intent: mode,
        })

        let fullText = ""
        const uiComponents: Array<{ name: string; data: Record<string, unknown> }> = []

        for await (const event of readSseEvents(response)) {
          debugLog("submit-sse", event.type, event)
          if (event.type === "text_delta" && event.text) {
            fullText += event.text
          } else if (event.type === "ui_component") {
            const { type, ...componentData } = event
            uiComponents.push({
              name: componentData.component as string,
              data: componentData as Record<string, unknown>,
            })
          } else if (event.type === "done") {
            if (event.thread_id != null) setThreadId(event.thread_id)
            else if (event.issue_id != null) setThreadId(event.issue_id)
            markAssistantReplied()
          } else if (event.type === "error") {
            throw new Error(event.message || "Stream error")
          }
        }
        debugLog(
          "submit-done",
          `text=${fullText.length}`,
          `ui=${uiComponents.map((c) => c.name).join(",") || "none"}`,
        )

        // Seed with an empty text part so a confirm-only message
        // (the SUBMIT_INSTRUCTION's happy path: AI outputs ONLY the
        // ``<<<UI>>>...<<<END_UI>>>`` block) renders its data part
        // reliably. assistant-ui has been observed to leave a bubble
        // visually empty when ``threadRuntime.append`` is called with
        // pure data content; including an empty text part mirrors the
        // text+data shape produced by the persisted-load path
        // (``to-thread-messages``), which renders correctly.
        const content: StreamContent = [
          { type: "text", text: fullText.trim() ? fullText : "" },
        ]
        for (const comp of uiComponents) {
          content.push({ type: "data", name: comp.name, data: comp.data })
        }

        debugLog(
          "submit-append",
          content
            .map((p) =>
              p.type === "text"
                ? `text(${p.text.length})`
                : `data(${p.name})`,
            )
            .join(","),
        )
        threadRuntime.append({
          role: "assistant",
          content,
          startRun: false,
        })

        if (uiComponents.some((c) => c.name === "confirm")) {
          setSubmitMode("idle")
        } else {
          setSubmitMode("armed")
          // Surface the arming explicitly — without this users can easily
          // miss that the button label changed to "Submit anyway" and
          // why the AI is asking a follow-up question instead of filing.
          toast.info("The assistant needs a bit more detail — answer, or click Submit anyway to file as-is.")
        }
      } finally {
        setIsRunning(false)
      }
    },
    [
      isRunning,
      threadRuntime,
      threadId,
      pageContext,
      setThreadId,
      setSubmitMode,
      markAssistantReplied,
    ],
  )

  return { trigger, isRunning }
}
